import React, { useCallback, useEffect, useState } from "react";
import { fetchProducts, createProduct, updateProduct, deleteProduct, fetchLeads, fetchAnalytics, fetchAdminProviders, rotateProviderToken, updateProviderContact, reseedCatalog, formatTT } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Pencil, Trash2, Plus, Sparkles, TrendingUp, Copy, RefreshCw, ExternalLink, Building2, DatabaseZap, Mail } from "lucide-react";
import { toast } from "sonner";
import AdminGate, { AdminLogoutButton } from "../components/AdminGate";

const EMPTY = {
    type: "loan", category: "personal", name: "", provider: "",
    interest_rate: 0, min_amount: 0, max_amount: 0,
    min_tenure_months: 0, max_tenure_months: 0,
    annual_fee: 0, processing_fee_pct: 0,
    min_income: 0, min_credit_score: 0,
    features: "", pros: "", cons: "",
    eligibility: "", description: "", image_url: "",
    featured: false, featured_priority: 0, sponsor_tier: ""
};

const Admin = ({ onLogout }) => {
    const [products, setProducts] = useState([]);
    const [leads, setLeads] = useState([]);
    const [analytics, setAnalytics] = useState({ by_provider: [], totals: {} });
    const [providers, setProviders] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(() => {
        fetchProducts().then(setProducts);
        fetchLeads().then(setLeads).catch(() => setLeads([]));
        fetchAnalytics().then(setAnalytics).catch(() => setAnalytics({ by_provider: [], totals: {} }));
        fetchAdminProviders().then(setProviders).catch(() => setProviders([]));
    }, []);

    const onCopyLink = (token) => {
        const url = `${window.location.origin}/portal/${token}`;
        navigator.clipboard.writeText(url).then(() => toast.success("Portal link copied to clipboard"));
    };

    const onRotateToken = async (provider) => {
        if (!window.confirm(`Rotate token for ${provider.name}? Their existing portal link will stop working.`)) return;
        try {
            await rotateProviderToken(provider.id);
            toast.success("Token rotated");
            fetchAdminProviders().then(setProviders);
        } catch {
            toast.error("Rotation failed");
        }
    };

    const onReseed = async () => {
        if (!window.confirm("Reseed the catalog?\n\nThis wipes ALL current products + reviews and replaces them with the SmartFinanceTT seed data (21 products). Leads are preserved.")) return;
        try {
            const res = await reseedCatalog();
            toast.success(`Reseeded ${res?.seeded ?? ""} products`);
            load();
        } catch (err) {
            toast.error(err.response?.status === 401 ? "Sign in again" : "Reseed failed");
        }
    };

    const onSaveContact = async (provider, email) => {
        try {
            await updateProviderContact(provider.id, email);
            toast.success(`Contact saved for ${provider.name}`);
            fetchAdminProviders().then(setProviders);
        } catch {
            toast.error("Could not save contact email");
        }
    };

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            ...EMPTY,
            ...p,
            features: (p.features || []).join("\n"),
            pros: (p.pros || []).join("\n"),
            cons: (p.cons || []).join("\n"),
        });
        setDialogOpen(true);
    };

    const onSave = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            interest_rate: Number(form.interest_rate),
            min_amount: Number(form.min_amount),
            max_amount: Number(form.max_amount),
            min_tenure_months: Number(form.min_tenure_months),
            max_tenure_months: Number(form.max_tenure_months),
            annual_fee: Number(form.annual_fee),
            processing_fee_pct: Number(form.processing_fee_pct),
            min_income: Number(form.min_income),
            min_credit_score: Number(form.min_credit_score),
            featured: !!form.featured,
            featured_priority: Number(form.featured_priority) || 0,
            sponsor_tier: form.sponsor_tier || "",
            features: form.features.split("\n").map(s => s.trim()).filter(Boolean),
            pros: form.pros.split("\n").map(s => s.trim()).filter(Boolean),
            cons: form.cons.split("\n").map(s => s.trim()).filter(Boolean),
        };
        try {
            if (editing) {
                await updateProduct(editing.id, payload);
                toast.success("Updated");
            } else {
                await createProduct(payload);
                toast.success("Created");
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            toast.error("Save failed");
        }
    };

    const onDelete = async (p) => {
        if (!window.confirm(`Delete "${p.name}"?`)) return;
        await deleteProduct(p.id);
        toast.success("Deleted");
        load();
    };

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                    <div className="label-overline text-[#718096] mb-2">Internal</div>
                    <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Admin Panel</h1>
                </div>
                <AdminLogoutButton onLogout={onLogout} />
            </div>

            <Tabs defaultValue="products">
                <TabsList className="bg-transparent border-b border-[#0B192C]/10 rounded-none w-full justify-start gap-6 mb-6">
                    <TabsTrigger value="products" data-testid="tab-products" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A2B] data-[state=active]:shadow-none label-overline">Products ({products.length})</TabsTrigger>
                    <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A2B] data-[state=active]:shadow-none label-overline">Leads ({leads.length})</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A2B] data-[state=active]:shadow-none label-overline">Analytics</TabsTrigger>
                    <TabsTrigger value="providers" data-testid="tab-providers" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A2B] data-[state=active]:shadow-none label-overline">Providers ({providers.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="products">
                    <div className="flex justify-end mb-4 gap-2">
                        <button onClick={onReseed} data-testid="admin-reseed-btn" className="px-4 py-2.5 label-overline border border-[#0B192C]/30 hover:border-[#0B192C] flex items-center gap-2" title="Wipe products+reviews, reload SmartFinanceTT seed">
                            <DatabaseZap className="w-3.5 h-3.5" strokeWidth={1.5}/> Reseed Catalog
                        </button>
                        <button onClick={openCreate} data-testid="admin-add-btn" className="btn-accent px-5 py-2.5 label-overline flex items-center gap-2">
                            <Plus className="w-3.5 h-3.5" strokeWidth={2}/> Add Product
                        </button>
                    </div>

                    <div className="grid-card bg-white overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-products-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Name</th>
                                    <th className="text-left p-4 label-overline">Provider</th>
                                    <th className="text-left p-4 label-overline">Type</th>
                                    <th className="text-left p-4 label-overline">Rate</th>
                                    <th className="text-left p-4 label-overline">Rating</th>
                                    <th className="text-left p-4 label-overline">Sponsor</th>
                                    <th className="text-right p-4 label-overline">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className="border-b border-[#0B192C]/5 hover:bg-[#F8F7F4]">
                                        <td className="p-4 font-serif-display text-base">{p.name}</td>
                                        <td className="p-4 font-mono-body">{p.provider}</td>
                                        <td className="p-4 font-mono-body text-xs uppercase">{p.type === "credit_card" ? "Card" : p.category}</td>
                                        <td className="p-4 font-mono-body">{p.interest_rate}%</td>
                                        <td className="p-4 font-mono-body">{p.rating}</td>
                                        <td className="p-4 font-mono-body text-xs">
                                            {p.featured ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0B192C] text-[#D1A77E]" data-testid={`admin-featured-tag-${p.id}`}>
                                                    <Sparkles className="w-3 h-3" strokeWidth={1.5}/>
                                                    {(p.sponsor_tier || "featured").toUpperCase()}
                                                </span>
                                            ) : <span className="text-[#718096]">—</span>}
                                        </td>
                                        <td className="p-4 text-right">
                                            <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="text-[#0B192C] hover:text-[#1E3A2B] mr-3"><Pencil className="w-4 h-4 inline" strokeWidth={1.5}/></button>
                                            <button onClick={() => onDelete(p)} data-testid={`admin-delete-${p.id}`} className="text-[#0B192C] hover:text-[#C53030]"><Trash2 className="w-4 h-4 inline" strokeWidth={1.5}/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="leads">
                    <div className="grid-card bg-white overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-leads-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Date</th>
                                    <th className="text-left p-4 label-overline">Name</th>
                                    <th className="text-left p-4 label-overline">Email</th>
                                    <th className="text-left p-4 label-overline">Phone</th>
                                    <th className="text-left p-4 label-overline">Product</th>
                                    <th className="text-left p-4 label-overline">Source</th>
                                    <th className="text-left p-4 label-overline">Income</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.length === 0 ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-[#718096]">No leads yet.</td></tr>
                                ) : leads.map((l) => (
                                    <tr key={l.id} className="border-b border-[#0B192C]/5">
                                        <td className="p-4 font-mono-body text-xs">{new Date(l.created_at).toLocaleString()}</td>
                                        <td className="p-4">{l.full_name}</td>
                                        <td className="p-4 font-mono-body text-xs">{l.email}</td>
                                        <td className="p-4 font-mono-body text-xs">{l.phone}</td>
                                        <td className="p-4 font-serif-display">{l.product_name}</td>
                                        <td className="p-4">
                                            {l.lead_source === "mortgage_calculator" ? (
                                                <span
                                                    title={l.mortgage_context ? `Property TT$${(l.mortgage_context.property_price || 0).toLocaleString()} · Mortgage TT$${(l.mortgage_context.mortgage_amount || 0).toLocaleString()} · ${l.mortgage_context.term_years || 0}yr @ ${(l.mortgage_context.interest_rate || 0).toFixed(2)}%` : ""}
                                                    className="inline-block px-2 py-0.5 bg-[#0A4F49] text-[#F8F7F4] text-xs cursor-help"
                                                >
                                                    Mortgage Calc
                                                </span>
                                            ) : (
                                                <span className="text-xs text-[#718096]">Product page</span>
                                            )}
                                        </td>
                                        <td className="p-4 font-mono-body">{formatTT(l.monthly_income)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="analytics">
                    <div className="grid md:grid-cols-5 gap-3 mb-6" data-testid="analytics-totals">
                        <KPI label="Products" value={analytics.totals?.products || 0} />
                        <KPI label="Featured" value={analytics.totals?.featured || 0} accent />
                        <KPI label="Impressions" value={(analytics.totals?.impressions || 0).toLocaleString()} />
                        <KPI label="Apply Clicks" value={(analytics.totals?.clicks || 0).toLocaleString()} />
                        <KPI label="Leads" value={analytics.totals?.leads || 0} />
                    </div>

                    <div className="grid-card bg-white overflow-x-auto">
                        <div className="px-4 pt-4 pb-2 border-b border-[#0B192C]/10 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#1E3A2B]" strokeWidth={1.5}/>
                            <span className="label-overline">Per-Provider Performance</span>
                        </div>
                        <table className="w-full text-sm" data-testid="analytics-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Provider</th>
                                    <th className="text-right p-4 label-overline">Products</th>
                                    <th className="text-right p-4 label-overline">Featured</th>
                                    <th className="text-right p-4 label-overline">Impressions</th>
                                    <th className="text-right p-4 label-overline">Clicks</th>
                                    <th className="text-right p-4 label-overline">Leads</th>
                                    <th className="text-right p-4 label-overline">Conversion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(analytics.by_provider || []).length === 0 ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-[#718096]">No analytics yet — interact with the site to populate.</td></tr>
                                ) : analytics.by_provider.map((r) => (
                                    <tr key={r.provider} className="border-b border-[#0B192C]/5">
                                        <td className="p-4 font-serif-display text-base">{r.provider}</td>
                                        <td className="p-4 text-right font-mono-body">{r.products}</td>
                                        <td className="p-4 text-right font-mono-body">
                                            {r.featured_products > 0 ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0B192C] text-[#D1A77E] text-xs">
                                                    <Sparkles className="w-3 h-3" strokeWidth={1.5}/>{r.featured_products}
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td className="p-4 text-right font-mono-body">{r.impressions.toLocaleString()}</td>
                                        <td className="p-4 text-right font-mono-body">{r.clicks.toLocaleString()}</td>
                                        <td className="p-4 text-right font-mono-body">{r.leads}</td>
                                        <td className="p-4 text-right font-mono-body">{r.conversion_rate}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid-card bg-[#EAE7E1] p-5 mt-6 text-xs text-[#4A5568] leading-relaxed max-w-3xl">
                        <strong className="font-serif-display text-base text-[#0B192C] block mb-1">How monetisation works</strong>
                        Sponsored placements (Featured products) are surfaced first in catalog & comparison results and labelled &ldquo;Sponsored&rdquo; to maintain transparency. Each &ldquo;Apply Now&rdquo; click is tracked here for affiliate billing — providers pay per qualified click or lead.
                    </div>
                </TabsContent>

                <TabsContent value="providers">
                    <div className="grid-card bg-[#EAE7E1] p-5 mb-6 text-xs text-[#4A5568] leading-relaxed max-w-3xl">
                        <strong className="font-serif-display text-base text-[#0B192C] block mb-1">Provider self-service portals</strong>
                        Each provider gets a tokenised link to view their own analytics & leads — no login, no admin access. Share these with your sales prospects, then rotate tokens any time to revoke access.
                    </div>

                    <div className="grid-card bg-white overflow-x-auto">
                        <div className="px-4 pt-4 pb-2 border-b border-[#0B192C]/10 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#1E3A2B]" strokeWidth={1.5}/>
                            <span className="label-overline">Providers</span>
                        </div>
                        <table className="w-full text-sm" data-testid="providers-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Provider</th>
                                    <th className="text-left p-4 label-overline">Contact Email (for lead alerts)</th>
                                    <th className="text-left p-4 label-overline">Portal Link</th>
                                    <th className="text-right p-4 label-overline">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {providers.length === 0 ? (
                                    <tr><td colSpan={4} className="p-8 text-center text-[#718096]">No providers indexed.</td></tr>
                                ) : providers.map((p) => {
                                    const url = `${window.location.origin}/portal/${p.access_token}`;
                                    return (
                                        <tr key={p.id} className="border-b border-[#0B192C]/5" data-testid={`provider-row-${p.id}`}>
                                            <td className="p-4 font-serif-display text-base">{p.name}</td>
                                            <td className="p-4">
                                                <ProviderContactInput provider={p} onSave={onSaveContact} />
                                            </td>
                                            <td className="p-4 font-mono-body text-xs text-[#4A5568]">
                                                <div className="flex items-center gap-2">
                                                    <span className="truncate max-w-[220px]" data-testid={`provider-link-${p.id}`}>{url}</span>
                                                    <a href={`/portal/${p.access_token}`} target="_blank" rel="noopener noreferrer" className="text-[#1E3A2B] hover:underline">
                                                        <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => onCopyLink(p.access_token)}
                                                    data-testid={`provider-copy-${p.id}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-2 border border-[#0B192C]/30 hover:border-[#0B192C] label-overline text-xs"
                                                >
                                                    <Copy className="w-3 h-3" strokeWidth={1.5}/> Copy
                                                </button>
                                                <button
                                                    onClick={() => onRotateToken(p)}
                                                    data-testid={`provider-rotate-${p.id}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#C53030]/40 text-[#C53030] hover:border-[#C53030] label-overline text-xs"
                                                >
                                                    <RefreshCw className="w-3 h-3" strokeWidth={1.5}/> Rotate
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#F8F7F4] rounded-none" data-testid="admin-product-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif-display text-3xl">{editing ? "Edit" : "Add"} Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSave} className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Type">
                                <Select value={form.type} onValueChange={(v) => update("type", v)}>
                                    <SelectTrigger data-testid="admin-type" className="rounded-none border-[#0B192C]/20 bg-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="loan">Loan</SelectItem>
                                        <SelectItem value="credit_card">Credit Card</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label="Category">
                                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                                    <SelectTrigger className="rounded-none border-[#0B192C]/20 bg-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="personal">Personal</SelectItem>
                                        <SelectItem value="auto">Auto</SelectItem>
                                        <SelectItem value="mortgage">Mortgage</SelectItem>
                                        <SelectItem value="business">Business / SME</SelectItem>
                                        <SelectItem value="credit_card">Credit Card</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label="Name"><Input data-testid="admin-name" value={form.name} onChange={(e) => update("name", e.target.value)} required className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                            <FormField label="Provider"><Input data-testid="admin-provider" value={form.provider} onChange={(e) => update("provider", e.target.value)} required className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                            <FormField label="Interest Rate %"><Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => update("interest_rate", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                            {form.type === "loan" ? (
                                <>
                                    <FormField label="Min Amount"><Input type="number" value={form.min_amount} onChange={(e) => update("min_amount", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                                    <FormField label="Max Amount"><Input type="number" value={form.max_amount} onChange={(e) => update("max_amount", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                                    <FormField label="Min Tenure (mo)"><Input type="number" value={form.min_tenure_months} onChange={(e) => update("min_tenure_months", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                                    <FormField label="Max Tenure (mo)"><Input type="number" value={form.max_tenure_months} onChange={(e) => update("max_tenure_months", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                                    <FormField label="Processing Fee %"><Input type="number" step="0.01" value={form.processing_fee_pct} onChange={(e) => update("processing_fee_pct", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                                </>
                            ) : (
                                <FormField label="Annual Fee"><Input type="number" value={form.annual_fee} onChange={(e) => update("annual_fee", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                            )}
                            <FormField label="Min Income"><Input type="number" value={form.min_income} onChange={(e) => update("min_income", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                            <FormField label="Min Credit Score"><Input type="number" value={form.min_credit_score} onChange={(e) => update("min_credit_score", e.target.value)} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                        </div>
                        <FormField label="Description"><Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>
                        <FormField label="Features (one per line)"><Textarea value={form.features} onChange={(e) => update("features", e.target.value)} rows={3} className="rounded-none border-[#0B192C]/20 bg-white font-mono-body text-sm" /></FormField>
                        <FormField label="Pros (one per line)"><Textarea value={form.pros} onChange={(e) => update("pros", e.target.value)} rows={2} className="rounded-none border-[#0B192C]/20 bg-white font-mono-body text-sm" /></FormField>
                        <FormField label="Cons (one per line)"><Textarea value={form.cons} onChange={(e) => update("cons", e.target.value)} rows={2} className="rounded-none border-[#0B192C]/20 bg-white font-mono-body text-sm" /></FormField>
                        <FormField label="Eligibility"><Textarea value={form.eligibility} onChange={(e) => update("eligibility", e.target.value)} rows={2} className="rounded-none border-[#0B192C]/20 bg-white" /></FormField>

                        <div className="border-t border-[#0B192C]/10 pt-4">
                            <div className="label-overline text-[#1E3A2B] mb-3 flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5}/> Monetisation
                            </div>
                            <div className="grid grid-cols-3 gap-3 items-end">
                                <div className="flex items-center justify-between p-3 border border-[#0B192C]/20 bg-white">
                                    <Label htmlFor="admin-featured-switch" className="label-overline text-xs cursor-pointer">Sponsored</Label>
                                    <Switch
                                        id="admin-featured-switch"
                                        data-testid="admin-featured-switch"
                                        checked={!!form.featured}
                                        onCheckedChange={(v) => update("featured", v)}
                                    />
                                </div>
                                <FormField label="Tier">
                                    <Select value={form.sponsor_tier || "none"} onValueChange={(v) => update("sponsor_tier", v === "none" ? "" : v)}>
                                        <SelectTrigger data-testid="admin-sponsor-tier" className="rounded-none border-[#0B192C]/20 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None</SelectItem>
                                            <SelectItem value="gold">Gold</SelectItem>
                                            <SelectItem value="platinum">Platinum</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Priority (higher = top)">
                                    <Input
                                        type="number"
                                        data-testid="admin-featured-priority"
                                        value={form.featured_priority}
                                        onChange={(e) => update("featured_priority", e.target.value)}
                                        className="rounded-none border-[#0B192C]/20 bg-white"
                                    />
                                </FormField>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setDialogOpen(false)} className="flex-1 px-4 py-2.5 border border-[#0B192C]/30 label-overline">Cancel</button>
                            <button type="submit" data-testid="admin-save-btn" className="btn-accent flex-1 px-4 py-2.5 label-overline">{editing ? "Update" : "Create"}</button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const FormField = ({ label, children }) => (
    <div>
        <Label className="label-overline">{label}</Label>
        <div className="mt-1">{children}</div>
    </div>
);

const KPI = ({ label, value, accent = false }) => (
    <div className={`grid-card p-5 ${accent ? "bg-[#0B192C] text-[#F8F7F4]" : "bg-white"}`}>
        <div className={`label-overline mb-1 ${accent ? "opacity-70" : "text-[#718096]"}`}>{label}</div>
        <div className="font-serif-display text-3xl">{value}</div>
    </div>
);

const ProviderContactInput = ({ provider, onSave }) => {
    const [value, setValue] = useState(provider.contact_email || "");
    const dirty = value !== (provider.contact_email || "");
    return (
        <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-[#718096]" strokeWidth={1.5}/>
            <input
                type="email"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="contact@bank.tt"
                data-testid={`provider-contact-${provider.id}`}
                className="flex-1 max-w-[260px] px-2 py-1 border border-[#0B192C]/20 bg-white font-mono-body text-xs focus:outline-none focus:border-[#0B192C]"
            />
            {dirty && (
                <button
                    onClick={() => onSave(provider, value)}
                    data-testid={`provider-contact-save-${provider.id}`}
                    className="px-2 py-1 bg-[#1E3A2B] text-[#F8F7F4] label-overline text-[10px]"
                >
                    Save
                </button>
            )}
        </div>
    );
};

const AdminWrapped = () => (
    <AdminGate>
        <Admin />
    </AdminGate>
);

export default AdminWrapped;
